// src/components/dashboard/QuickInsights.tsx - Quick Insights component with random tips
import React, { useState, useEffect } from 'react';
import { getRandomTip, RoommateTip } from '../../data/roommateTips';

interface QuickInsightsProps {
  userProfileCompleteness?: number;
}

const QuickInsights: React.FC<QuickInsightsProps> = ({ userProfileCompleteness }) => {
  const [currentTip, setCurrentTip] = useState<RoommateTip>(getRandomTip());

  // Get a personalized tip based on profile completeness
  useEffect(() => {
    if (userProfileCompleteness !== undefined && userProfileCompleteness < 100) {
      // If profile is incomplete, prioritize profile tips
      const profileTip = getRandomTip('profile');
      setCurrentTip(profileTip);
    } else {
      // Otherwise, show random tip
      setCurrentTip(getRandomTip());
    }
  }, [userProfileCompleteness]);

  const handleRefreshTip = () => {
    setCurrentTip(getRandomTip());
  };

  // Auto-refresh tip every 30 seconds (optional)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTip(getRandomTip());
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">💡</span>
          <h2 className="text-xl font-bold text-coffee">TIPS & INSIGHTS</h2>
        </div>
        <button
          onClick={handleRefreshTip}
          className="text-coffee hover:text-terracotta text-sm transition-colors"
          title="Get another tip"
        >
          🔄
        </button>
      </div>
      
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          {currentTip.icon && (
            <span className="text-2xl flex-shrink-0">{currentTip.icon}</span>
          )}
          <p className="text-coffee text-sm leading-relaxed flex-1">
            {currentTip.tip}
          </p>
        </div>
        
        {/* Category badge */}
        <div className="flex items-center gap-2">
          <span 
            className="text-xs text-cream capitalize px-2 py-1 rounded border"
            style={{
              backgroundColor: 'rgb(232, 160, 102)', // Terracotta
              borderColor: 'rgb(89, 74, 66)' // Coffee
            }}
          >
            {currentTip.category === 'profile' && 'Profile Optimization'}
            {currentTip.category === 'communication' && 'Communication'}
            {currentTip.category === 'compatibility' && 'Compatibility'}
            {currentTip.category === 'safety' && 'Safety'}
            {currentTip.category === 'budget' && 'Budget & Logistics'}
            {currentTip.category === 'success' && 'Success Tips'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default QuickInsights;

